import React, { useState, useEffect, useRef } from "react";
import clsx from "clsx";
import { makeStyles } from '@material-ui/styles';
import { withRouter, Link } from "react-router-dom";
import {
  CircularProgress, FormHelperText
} from '@material-ui/core';
import withShowToast from '../../../hocs/with_show_toast.js';
import { compose } from 'redux';
import moment from "moment";

import I18n from "../../../base/i18n.js";

import Container from "../../../base/components/container.js";
import HtmlContent from "../../../base/components/html_content.js";
import BackButton from "../../../base/buttons/back_button.js";
import Image from "../../../base/components/image.js";
import List from "../../../base/components/list.js";

import DateTimeFormatter from "../../../utils/date_time_formatter.js";
import MoneyFormatter from "../../../utils/money_formatter.js";
import PercentFormatter from "../../../utils/percent_formatter.js";

import InvestmentStatusEnum from "../../../enums/investment_status.js";
import InvestmentActivityStatusEnum from "../../../enums/investment_activity_status.js";

import { ProjectModel } from "../../../models/projects/project_model.js";
import { ProjectInvestmentModel } from "../../../models/projects/project_investment.js";
import { PaymentTransactionModel } from "../../../models/payments/payment_transaction_model.js";

import IcBronzeCheck from "../../../resources/svgs/ic_bronze_check.svg";
import IcDownload from "../../../resources/svgs/ic_download.svg";

import ShowInvestmentUseCase from "../../../use_cases/projects/investments/show_investment_use_case.js";

import InvestmentPaymentTransactionChannel from "../../../channels/investment/investment_payment_transaction_channel.js";

import Routes from "../../routes.js";
import SmartLink from "../../../base/links/smart_link.js.jsx";

import {
  NotFoundErrorCode, ForbiddenErrorCode, UnauthorizedErrorCode
} from "../../../constants.js";

const pageStyles = makeStyles(theme => ({
  root: {
    backgroundColor: "white",
    "& h1": {
      fontSize: "38px",
      lineHeight: "50px",
      fontWeight: theme.typography.fontWeightMedium,
      textTransform: "uppercase",
      margin: "0px 44px 0px 0px",
      ['@media (max-width: 768px)']: {
        fontSize: "20px",
        lineHeight: "26px",
        margin: "0px 0px 8px",
      },
    },
    "& h2": {
      fontSize: "28px",
      lineHeight: "36px",
      fontWeight: theme.typography.fontWeightMedium,
      textTransform: "uppercase",
      margin: "0px 0px 32px",
      ['@media (max-width: 768px)']: {
        fontSize: "20px",
        lineHeight: "26px",
        margin: "0px 0px 16px",
      },
    }
  },
  container: {
    padding: "50px 16px 100px",
    fontSize: "20px",
    color: theme.palette.black,
    ['@media (max-width: 768px)']: {
      padding: "24px 24px",
    },
  },
  linkRow: {
    display: "flex",
    alignItems: "center",
    marginBottom: "43px",
    ['@media (max-width: 768px)']: {
      margin: "0px -4px 22px"
    },
    "& $projectLink": {
      display: "none",
      ['@media (max-width: 768px)']: {
        display: "block",
      },
    }
  },
  backButton: {
    ['@media (max-width: 768px)']: {
      fontSize: "12px",
      lineHeight: "16px"
    },
  },
  titleRow: {
    display: "flex",
    alignItems: "center",
    fontWeight: theme.typography.fontWeightMedium,
    marginBottom: "62px",
    "& $projectLink": {
      display: "block",
      ['@media (max-width: 768px)']: {
        display: "none",
      },
    },
    ['@media (max-width: 768px)']: {
      flexWrap: "wrap",
      marginBottom: "16px",
    },
  },
  status: {
    padding: "6px 30px",
    fontSize: "18px",
    lineHeight: "20px",
    textTransform: "uppercase",
    minWidth: "160px",
    textAlign: "center",
    ['@media (max-width: 768px)']: {
      fontSize: "10px",
      lineHeight: "20px",
      width: "100%",
      padding: "0px 16px",
    },
  },
  applied: {
    background: "rgba(157, 198, 71, 0.2)"
  },
  paid: {
    background: "rgba(182, 123, 100, 0.2)"
  },
  paidBack: {
    background: theme.palette.grayBg
  },
  canceled: {
    background: theme.palette.gray2,
    color: "white"
  },
  tooltip: {
    fontSize: "14px",
    padding: "5px",
    textAlign: "center"
  },
  spacer: {
    flexGrow: "1"
  },
  projectLink: {
    color: theme.palette.grayText,
    fontSize: "14px",
    lineHeight: "16px",
    display: "block",
    textDecoration: "none",
    textTransform: "uppercase",
    "&:hover": {
      textDecoration: "underline",
    },
    ['@media (max-width: 768px)']: {
      fontSize: "12px",
      lineHeight: "16px"
    },
  },
  summary: {
    display: "flex",
    marginBottom: "62px",
    ['@media (max-width: 768px)']: {
      flexWrap: "wrap",
      marginBottom: "32px",
    },
  },
  imageColumn: {
    flexBasis: "292px",
    height: "255px",
    flexShrink: 0,
    marginRight: "48px",
    ['@media (max-width: 768px)']: {
      marginRight: "0px",
      marginBottom: "24px",
      flexBasis: "100%",
      height: "178px",
    },
  },
  image: {
    width: "100%",
    height: "100%",
  },
  desktop: {
    display: "block",
    ['@media (max-width: 768px)']: {
      display: "none"
    },
  },
  mobile: {
    display: "none",
    ['@media (max-width: 768px)']: {
      display: "block"
    },
  },
  infoRow: {
    "&:first-child $infoColumn": {
      paddingTop: "0px"
    },
    "&:last-child $infoColumn": {
      paddingBottom: "0px"
    },
    ['@media (max-width: 768px)']: {
      display: "flex",
    },
  },
  infoColumn: {
    flexGrow: "1",
    minHeight: "26px",
    fontSize: "18px",
    textAlign: "left",
    verticalAlign: "top",
    fontWeight: theme.typography.fontWeightRegular,
    padding: "7px 0px",
    ['@media (max-width: 768px)']: {
      fontSize: "16px",
      lineHeight: "20px",
      padding: "5px 0px",
    },
  },
  labelColumn: {
    minWidth: "160px",
    paddingRight: "16px",
    ['@media (max-width: 768px)']: {
      minWidth: "0px",
      paddingRight: "8px",
    },
  },
  valueColumn: {
    ['@media (max-width: 768px)']: {
      flexGrow: "1"
    },
  },
  paymentInformation: {
    fontWeight: theme.typography.fontWeightLight,
    fontSize: "18px",
    lineHeight: "26px",
    textAlign: "justify",
    marginBottom: "60px",
    ['@media (max-width: 768px)']: {
      fontSize: "14px",
      lineHeight: "20px",
      textAlign: "left",
      marginBottom: "35px",
    },
  },
  paymentInformationDescription: {
    marginBottom: "24px",
    ['@media (max-width: 768px)']: {
      marginBottom: "26px",
    },
  },
  paymentInformationList: {
    marginBottom: "22px",
    "& li:before": {
      backgroundImage: `url(${IcBronzeCheck})`,
    },
  },
  paymentInformationLoaderContainer: {
    maxWidth: "66%",
    margin: "0 auto 22px",
    textAlign: "center",
    ['@media (max-width: 768px)']: {
      maxWidth: "100%",
    },
  },
  paymentInformationLoaderIndicator: {
    margin: "4px 0px 23px"
  },
  paymentInformationLoaderText: {
    fontSize: "14px",
    lineHeight: "18px",
    textAlign: "center",
    ['@media (max-width: 768px)']: {
      lineHeight: "16px",
      textAlign: "left",
    },
  },
  documents: {
    fontWeight: theme.typography.fontWeightLight,
    fontSize: "18px",
    lineHeight: "26px",
    ['@media (max-width: 768px)']: {
      fontSize: "14px",
      lineHeight: "20px",
    },
  },
  fileList: {
    "& li:before": {
      backgroundImage: `url(${IcDownload})`,
    },
  },
  fileLink: {
    color: theme.palette.black,
    fontWeight: theme.typography.fontWeightLight,
    cursor: "pointer",
    textDecoration: "none",
    "&:hover": {
      textDecoration: "underline",
    }
  }
}));

const InvestmentPage = (props) => {

  const { history } = props;

  const [investment, setInvestment] = useState(undefined);
  const [errorMessage, setErrorMessage] = useState(undefined);
  const [loading, setLoading] = useState(true);
  const channelRef = useRef();

  const onInvestmentChanged = (data) => {
    const { investment, message } = data;
    if(investment) setInvestment(investment);
    setErrorMessage(message);
  }

  useEffect(() => {

    const { identifier } = props;
    if(!identifier) {
      history.push(Routes.pageNotFound());
    }
    ShowInvestmentUseCase.execute(identifier)
    .then((result) => {
      const { data: { investment } } = result;
      setInvestment(investment);
      setLoading(false);

      channelRef.current = new InvestmentPaymentTransactionChannel(investment);
      channelRef.current.subscribe(onInvestmentChanged);

    })
    .catch((result) => {
      setLoading(false);
      const { errorCode } = result;

      if(errorCode === UnauthorizedErrorCode) {
        props.showWarningMessage(I18n.t("errors.unauthorized"));
        history.replace(Routes.signIn());
        return;
      }
      props.showServerErrorIfNeed(result);
      if(errorCode === NotFoundErrorCode ||  errorCode === ForbiddenErrorCode) {
        history.replace(Routes.pageNotFound());
        return;
      }
    })

    // Equals to component will unmount
    return () => {
      if(channelRef.current) {
        channelRef.current.unsubscribe();
      }
    }
  }, [])

  const classes = pageStyles();

  const { status, activityStatus, createdAt,
          amount,
          paymentTransaction,
          contractPdf,
          signature
  } = investment || { };

  const project = (investment || {}).project || {};
  const { name, mainGalleryImage: { urls } = {}, documents } = project;

  let bonusRates = undefined
  let annualInterestRate = project.annualInterestRate

  if (project.bonusRates && signature) {
    const signedAt = signature.date

    bonusRates = project.bonusRates.filter(br => moment(signedAt).isSameOrBefore(br.deadline, 'day') ).sort((br1, br2) => new Date(br1.deadline) >= new Date(br2.deadline))
    if (bonusRates.length > 0) {
      const bonusRate = bonusRates[0].rate;
      annualInterestRate += bonusRate
    }
  }

  let src = undefined;
  let srcSet = {};

  if(urls) {
    src = urls.large;
    srcSet = {
      "2x": urls.large2x,
      "3x": urls.large3x
    }
  }

  const attributes = [
    {
      label: `${I18n.t("web.page.investment.info.status")}:`,
      value: activityStatus === "canceled" ? InvestmentActivityStatusEnum.label(activityStatus) : InvestmentStatusEnum.label(status)
    },
    {
      label: `${I18n.t("web.page.investment.info.created_at")}:`,
      value: DateTimeFormatter.format(createdAt)
    },
    {
      label: `${I18n.t("web.page.investment.info.amount")}:`,
      value: `${MoneyFormatter.format(amount)} ${I18n.t("currency.euro")}`
    },
    {
      label: `${I18n.t("web.page.investment.info.project_annual_interest_rate")}:`,
      value: PercentFormatter.format(annualInterestRate / 100),
    }
  ]

  const desktopInvestmentInfo = () => {
    return <table className = { classes.desktop } >
      <tbody>
        {
          attributes.map((attribute, index) => {
            const { label, value } = attribute;
            return <tr className = { classes.infoRow }
                       key = { index } >
            <td className = { clsx(classes.infoColumn, classes.labelColumn )} > { label }</td>
            <td className = { clsx(classes.infoColumn, classes.valueColumn )} > { value }</td>
          </tr>
          })
        }
      </tbody>
    </table>
  }

  const mobileInvestmentInfo = () => {
    return <div className = { classes.mobile } >
      {
        attributes.map((attribute, index) => {
          const { label, value } = attribute;
          return <div className = { classes.infoRow }
                      key = { index } >
            <div className = { clsx(classes.infoColumn, classes.labelColumn )} >
              { `${label} ${value}` }
            </div>
        </div>
        })
      }
    </div>
  }

  const paymentInformation = () => {
    if(DateTimeFormatter.isBefore(createdAt, "2023-02-01")) return <React.Fragment />
    return (
      <div className = { classes.paymentInformation } >
        <h2>
          { I18n.t("web.page.investment.payment_information.title") }
        </h2>
        <HtmlContent className = { classes.paymentInformationDescription } >
          { I18n.t("web.page.investment.payment_information.first_text", {amount: I18n.toCurrency(amount, {unit: ""})}) }
        </HtmlContent>
        {
          !paymentTransaction && !errorMessage &&
          <div className = { classes.paymentInformationLoaderContainer } >
            <CircularProgress className = { classes.paymentInformationLoaderIndicator }
                              size = { 30 } />
            <div className = { classes.paymentInformationLoaderText }>
              { I18n.t("web.page.investment.payment_information.loading") }
            </div>

          </div>
        }
        {
          paymentTransaction &&
          <List className = { classes.paymentInformationList } >
            <li>{ `${PaymentTransactionModel.fieldDisplayName("owner")}: ${paymentTransaction.owner}` }</li>
            <li>{ `${PaymentTransactionModel.fieldDisplayName("iban")}: ${paymentTransaction.iban}` }</li>
            <li>{ `${PaymentTransactionModel.fieldDisplayName("bic")}: ${paymentTransaction.bic}` }</li>
            <li>{ `${PaymentTransactionModel.fieldDisplayName("transferPurpose")}: ${paymentTransaction.transferPurpose}` }</li>
          </List>
        }
        {
          errorMessage &&
          <div className = { classes.paymentInformationLoaderContainer } >
            <FormHelperText error = { true } key = { errorMessage } >
              { errorMessage }
            </FormHelperText>
          </div>
        }
        <HtmlContent className = { classes.paymentInformationDescription } >
          { I18n.t("web.page.investment.payment_information.second_text") }
        </HtmlContent>
        <List className = { classes.fileList } >
          {
            contractPdf &&
            <li>
              <a href = { contractPdf.url }
                 target = { "_blank" }
                 className = { classes.fileLink } >
                { ProjectInvestmentModel.fieldDisplayName("contract") }
              </a>
            </li>
          }
        </List>
      </div>
    );

  }

  const content = () => {
    if(loading || !investment) {
      return (
        <React.Fragment />
      );
    }

    return (
      <React.Fragment>
        <div className = { classes.linkRow } >
          <BackButton className = { classes.backButton } />
          <div className = { classes.spacer } />
          <SmartLink to = { Routes.project(project) }
                className = { classes.projectLink } >
            { I18n.t("web.page.investment.go_to_project") }
          </SmartLink>
        </div>
        <div className = { classes.titleRow } >
          <h1>
            { name }
          </h1>
          <div className = { classes.spacer } />
          <SmartLink to = { Routes.project(project) }
                className = { classes.projectLink } >
            { I18n.t("web.page.investment.go_to_project") }
          </SmartLink>
        </div>
        <div className = { classes.summary} >
          <div className = { classes.imageColumn } >
            <Image className = { classes.image }
                   src = { src }
                   alt = { name }
                   srcSet = { srcSet } />
          </div>
          <div className = { classes.infoColumn } >
            { desktopInvestmentInfo() }
            { mobileInvestmentInfo() }
          </div>
        </div>
        { paymentInformation() }
        { documents.length > 0 && <div className = { classes.documents} >
          <h2> { ProjectModel.fieldDisplayName("documents") } </h2>
          <List className = { classes.fileList } >
            {
              documents.filter(document => document.showInConfirmationPage).map((document) => {
                return <li key = { document.id } >
                  { document.pdf ? <a href = { document.pdf.url }
                     target = { "_blank" }
                     className = { classes.fileLink } >
                    { document.name }
                  </a> : document.name }
              </li>
              })
            }
          </List>
        </div> }
      </React.Fragment>
    );
  }

  return (
    <div className = { classes.root } >
      <Container className = { classes.container }>
        { content() }
      </Container>
    </div>
  );
}

export default compose(withRouter, withShowToast)(InvestmentPage);
